import React, { useEffect, useState } from 'react';
import { checkUserMessagingParticipation } from 'tradera/apps/my-tradera/app/helpers/messaging-helpers';
import ArrowRightIcon from '@tradera/blueprint/theme/icons/arrow-right.svg?jsx';
import CloseIcon from '@tradera/blueprint/theme/icons/close-narrow.svg?jsx';
import LoadIcon from '@tradera/blueprint/theme/icons/load.svg?jsx';
import { logger } from 'packages/logger';
import { useTranslator, useUrlLocalizer } from 'tradera/lang/translate';
import { getMessagingUrl } from 'tradera/components/messaging/get-messaging-url';
import { z } from 'zod';
import classNames from 'classnames';
import { MessagingInternationalIdentificationModal } from 'tradera/components/international-identification-modal/messaging-international-identification-modal';
import { Space } from '../atoms/space/space';
import { Button } from '../atoms/button/button';
import { Heading } from '../atoms/heading/heading';
import { Text } from '../atoms/text/text';
import MessageIcon from '@tradera/blueprint/theme/icons-colored/message-icon.svg?jsx';
import DialogModal from 'tradera/components/dialog/dialog-modal';
import { HeaderWithCloseButton } from 'tradera/components/scrollable-modal/header-with-close-button';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import css from './messaging-modal.module.scss';

const POST_MESSAGE_TYPE_PREFIX = 'tradera.messaging.';
const CLOSE_EVENT = `${POST_MESSAGE_TYPE_PREFIX}close` as const;
const OPEN_INTERNATIONAL_IDENTIFICATION_MODAL_EVENT =
    `${POST_MESSAGE_TYPE_PREFIX}openInternationalIdentificationModal` as const;

const MessageEventSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal(CLOSE_EVENT)
    }),
    z.object({
        type: z.literal(OPEN_INTERNATIONAL_IDENTIFICATION_MODAL_EVENT)
    })
]);

// Don't know where this comes from, so won't be using any generated type for now
type MessagingItem = {
    seller: { alias: string };
    transaction: { purchaseOrderId: string };
};

type MessagingModalProps = {
    title?: string;
    src: string;
    onModalClosed: () => void;
    recipientId: number;
    item: MessagingItem;
    participating?: boolean;
    isContactHighestBidder?: boolean;
};

export const MessagingModal = ({
    onModalClosed,
    recipientId,
    title,
    src,
    item,
    participating: participatingProp,
    isContactHighestBidder
}: MessagingModalProps) => {
    const { toLocalizedUrl } = useUrlLocalizer();
    const isMobile = useIsMobile();
    const [
        showInternationalIdentificationModal,
        setShowInternationalIdentificationModal
    ] = useState(false);

    const MESSAGING_REMOTE_URL = getMessagingUrl(toLocalizedUrl);
    const [participating, setParticipating] = React.useState<boolean | null>(
        participatingProp || null
    );
    const iframeRef = React.useRef<HTMLIFrameElement>(null);

    const handleMessage = React.useCallback(
        (event: MessageEvent) => {
            if (MESSAGING_REMOTE_URL.indexOf(event.origin) < 0) {
                return;
            }

            let parsed;
            try {
                parsed = MessageEventSchema.parse(event.data);
            } catch (error) {
                return;
            }
            const { type } = parsed;

            switch (type) {
                // Messages loaded & ready
                // Unread threads update
                case CLOSE_EVENT:
                    onModalClosed();
                    break;
                case OPEN_INTERNATIONAL_IDENTIFICATION_MODAL_EVENT:
                    setShowInternationalIdentificationModal(true);
                    break;
            }
        },
        [MESSAGING_REMOTE_URL, onModalClosed]
    );

    const handleClose = () => {
        onModalClosed();
    };

    useEffect(() => {
        if (iframeRef.current) {
            // Focus on iframe content window to allow focus to move into iframe
            setTimeout(() => {
                if (iframeRef.current && iframeRef.current.contentWindow) {
                    iframeRef.current.contentWindow.focus();
                }
            }, 0);
        }
    }, [iframeRef]);

    useEffect(() => {
        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [handleMessage]);

    useEffect(() => {
        if (participating === null) {
            checkUserMessagingParticipation(recipientId)
                .then((participating: boolean) =>
                    setParticipating(participating)
                )
                .catch(logger);
        }
    }, [participating, recipientId]);

    const isContactingHighestBidderButNotParticipating =
        isContactHighestBidder && participating === false;

    const modalTitle =
        !isContactingHighestBidderButNotParticipating && title ? title : '';

    const maxWidth = '43rem';

    return (
        <DialogModal
            className="w-100"
            onDismiss={handleClose}
            style={{
                maxWidth: isMobile
                    ? `min(${maxWidth}, 100%)`
                    : `min(${maxWidth}, 80vw)`
            }}>
            <div
                className={classNames(css.container, {
                    [css.fixedHeight]:
                        !isContactingHighestBidderButNotParticipating
                })}>
                <HeaderWithCloseButton
                    onClose={handleClose}
                    title={modalTitle}
                    border
                />
                <div
                    className={classNames(css.body, {
                        [css.bodyWithIframe]: participating
                    })}>
                    {participating === true && (
                        <>
                            <iframe
                                className={css.iframe}
                                src={MESSAGING_REMOTE_URL + src}
                                ref={iframeRef}
                                title="Messaging"
                            />
                            {showInternationalIdentificationModal && (
                                <MessagingInternationalIdentificationModal
                                    onCloseModal={() =>
                                        setShowInternationalIdentificationModal(
                                            false
                                        )
                                    }
                                    onVerifyComplete={() => {
                                        setShowInternationalIdentificationModal(
                                            false
                                        );
                                        iframeRef.current?.contentWindow?.location.reload();
                                    }}
                                />
                            )}
                        </>
                    )}
                    {isContactingHighestBidderButNotParticipating && (
                        <ContactHighestBidderNotParticipatingMessage
                            onClose={handleClose}
                        />
                    )}
                    {!isContactingHighestBidderButNotParticipating &&
                        participating === false && (
                            <NotParticipatingMessage
                                item={item}
                                onClose={handleClose}
                            />
                        )}
                    {participating === null && <Loader />}
                </div>
            </div>
        </DialogModal>
    );
};

const Loader = () => {
    return (
        <div className="d-flex h-100 w-100 justify-content-center align-items-center">
            <LoadIcon className="icon icon-secondary icon-lg icon-spin" />
        </div>
    );
};

type ContactHighestBidderNotParticipatingMessageProps = {
    onClose: () => void;
};

const ContactHighestBidderNotParticipatingMessage = ({
    onClose
}: ContactHighestBidderNotParticipatingMessageProps) => {
    const { toLocalizedUrl } = useUrlLocalizer();
    const { t } = useTranslator();

    return (
        <Space
            direction="vertical"
            align="center"
            gap={2}
            className="px-3 pb-3">
            <MessageIcon height={125} />
            <Heading as="h4" className="mt-1">
                {t('messaging_not_participating_title')}
            </Heading>
            <Text>{t('messaging_not_participating_content')}</Text>
            <Space gap={3} className="w-100">
                <Button variant="outline" fluid onClick={onClose}>
                    {t('messaging_not_participating_cancel')}
                </Button>
                <Button fluid href={toLocalizedUrl('/my/settings/email')}>
                    {t('messaging_not_participating_to_settings')}
                </Button>
            </Space>
        </Space>
    );
};

type NotParticipatingMessageProps = {
    item: MessagingItem;
    onClose: () => void;
};

const NotParticipatingMessage = ({
    item,
    onClose
}: NotParticipatingMessageProps) => {
    const { toLocalizedUrl } = useUrlLocalizer();

    if (item) {
        return (
            <div className="d-flex h-100 w-100 justify-content-center align-items-center">
                <div className="p-4">
                    <h2 className="text-center">{item.seller.alias}</h2>
                    <p className="text-center mb-5">
                        <span className="badge badge-dark">Butik</span>
                    </p>
                    <div style={{ maxWidth: '35rem' }}>
                        <p className="alert alert-secondary mb-5">
                            {item.seller.alias} är inte en privat säljare och
                            finns för tillfället inte tillgänglig via Traderas
                            meddelandetjänst.
                        </p>
                        <p className="mb-5">
                            Vi rekommenderar att du kontaktar dem via
                            orderinformation för detta köp.
                        </p>
                    </div>
                    <div
                        style={{ maxWidth: '30rem' }}
                        className="mx-auto align-items-center">
                        <Button
                            theme="primary"
                            fluid
                            className="mb-3"
                            icon={<ArrowRightIcon />}
                            iconConfig={{ position: 'right', variant: 'fixed' }}
                            href={toLocalizedUrl(
                                `/my/order/${item.transaction.purchaseOrderId}`
                            )}>
                            <span>Orderinformation</span>
                        </Button>
                        <Button
                            theme="action"
                            fluid
                            variant="outline"
                            className="btn--icon-fixed"
                            onClick={onClose}>
                            <CloseIcon className="icon icon-before" />
                            <span>Stäng</span>
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="d-flex h-100 w-100 justify-content-center align-items-center">
            <div style={{ maxWidth: '35rem' }}>
                <p className="alert alert-secondary mb-5">
                    Säljaren finns för tillfället inte tillgänglig via Traderas
                    meddelandetjänst.
                </p>
            </div>
            <div
                style={{ maxWidth: '30rem' }}
                className="mx-auto align-items-center">
                <Button
                    theme="action"
                    fluid
                    variant="outline"
                    className="btn--icon-fixed"
                    onClick={onClose}>
                    <CloseIcon className="icon icon-before" />
                    <span>Stäng</span>
                </Button>
            </div>
        </div>
    );
};
